import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import Icon from './Icon';
import Avatar from './Avatar';
import BrandMark from './BrandMark';
import { useBranding } from '../branding/BrandingProvider';
import useTheme from './useTheme';
import useWhatsAppUnread from './useWhatsAppUnread';

/**
 * App chrome: collapsible sidebar + sticky top bar.
 *
 * Add a nav item per feature; guard it with `can(...)` so users only see
 * what they can reach.
 */
export default function Layout() {
  const { user, isSuperAdmin, logout, can } = useAuth();
  const { name: crmName } = useBranding();
  const [theme, toggleTheme] = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const canChat = ['whatsapp.chats.view_all', 'whatsapp.chats.view_own', 'whatsapp.chats.initiate'].some(can);
  const unread = useWhatsAppUnread(canChat);

  const nav = [
    { to: '/', label: 'Dashboard', icon: 'dashboard', end: true, show: true },
    { to: '/crm/leads', label: 'Leads', icon: 'users', show: ['crm.leads.view_own', 'crm.leads.view_branch', 'crm.leads.view_all', 'crm.leads.update_own', 'crm.leads.update_branch', 'crm.leads.update_all'].some(can) },
    { to: '/crm/followups', label: 'Follow-ups', icon: 'calendar', show: ['crm.followups.view_own', 'crm.followups.view_branch', 'crm.followups.view_all', 'crm.followups.create_own', 'crm.followups.create_branch', 'crm.followups.create_all'].some(can) },
    {
      label: 'Devices',
      icon: 'mobile',
      show: ['crm.calls.view_all', 'crm.calls.view_branch', 'crm.calls.view_own', 'crm.devices.manage', 'crm.devices.manage_branch'].some(can),
      children: [
        { to: '/crm/devices', label: 'Devices', icon: 'mobile', show: ['crm.devices.manage', 'crm.devices.manage_branch'].some(can) },
        { to: '/crm/calls', label: 'Calls', icon: 'phone', show: ['crm.calls.view_all', 'crm.calls.view_branch', 'crm.calls.view_own'].some(can) },
      ],
    },
    {
      label: 'Complaints',
      icon: 'inbox',
      show: ['complaints.register', 'complaints.view_branch', 'complaints.view_all', 'complaints.assign_branch', 'complaints.assign_all', 'complaints.handle'].some(can),
      children: [
        { to: '/complaints', label: 'Complaints', icon: 'inbox', end: true, show: ['complaints.register', 'complaints.view_branch', 'complaints.view_all', 'complaints.assign_branch', 'complaints.assign_all'].some(can) },
        { to: '/complaints/assigned', label: 'Assigned Complaints', icon: 'inbox', show: can('complaints.handle') },
      ],
    },
    { to: '/attendance', label: 'My Attendance', icon: 'clock', end: true, show: can('attendance.own') },
    { to: '/attendance/staff', label: 'Staff Attendance', icon: 'calendar', show: ['attendance.view_all', 'attendance.manage_all', 'attendance.view_branch', 'attendance.manage_branch'].some(can) },
    {
      label: 'WhatsApp Admin',
      icon: 'chat',
      show: canChat || can('whatsapp.bot.configure') || can('whatsapp.templates.manage'),
      badge: unread,
      children: [
        { to: '/whatsapp/chats', label: 'WhatsApp', icon: 'chat', show: canChat, badge: unread },
        { to: '/whatsapp/bot', label: 'Bot', icon: 'bot', show: can('whatsapp.bot.configure') },
        { to: '/whatsapp/templates', label: 'Templates', icon: 'doc', show: can('whatsapp.templates.manage') },
      ],
    },
    {
      label: 'Masters',
      icon: 'tag',
      show: ['crm.statuses.manage', 'crm.sources.manage', 'crm.meta.manage', 'crm.website.manage', 'crm.custom-fields.manage', 'complaints.masters'].some(can),
      children: [
        { to: '/crm/statuses', label: 'Lead Statuses', icon: 'tag', show: can('crm.statuses.manage') },
        { to: '/masters/sources', label: 'Sources', icon: 'inbox', show: can('crm.sources.manage') },
        { to: '/masters/website', label: 'Website Leads', icon: 'bolt', show: can('crm.website.manage') },
        { to: '/masters/meta', label: 'Meta Leads', icon: 'bolt', show: can('crm.meta.manage') },
        { to: '/masters/complaints', label: 'Complaints', icon: 'inbox', show: can('complaints.masters') },
        { to: '/crm/custom-fields', label: 'CRM Fields', icon: 'key', show: can('crm.custom-fields.manage') },
      ],
    },
    { to: '/branches', label: 'Branches', icon: 'building', show: can('branches.manage') },
    { to: '/admins', label: 'Admins', icon: 'shield', show: can('admins.view') },
  ]
    .map((n) => (n.children ? { ...n, children: n.children.filter((c) => c.show) } : n))
    .filter((n) => n.show);

  const flat = nav.flatMap((n) => n.children || [n]);
  const matches = (n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to));
  const current = location.pathname.startsWith('/settings') ? { label: 'Settings' } : flat.find(matches);

  // A group is open while one of its pages is showing, or when the user opened it.
  const [openGroups, setOpenGroups] = useState({});
  const isOpen = (n) => openGroups[n.label] ?? n.children.some(matches);

  const cls = ['app-shell', collapsed && 'collapsed', mobileOpen && 'mobile-open']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cls}>
      <aside className="sidebar">
        <div className="brand">
          <BrandMark />
          <span className="brand-text">{crmName}</span>
        </div>

        <div className="nav-label">Menu</div>
        <nav>
          {nav.map((n) =>
            n.children ? (
              <div key={n.label} className="nav-group">
                <button
                  type="button"
                  className={`nav-group-btn${n.children.some(matches) ? ' has-active' : ''}`}
                  title={n.label}
                  aria-expanded={isOpen(n)}
                  onClick={() => setOpenGroups((g) => ({ ...g, [n.label]: !isOpen(n) }))}
                >
                  <Icon name={n.icon} />
                  <span className="nav-text">{n.label}</span>
                  {n.badge > 0 && !isOpen(n) && <span className="nav-badge">{n.badge > 99 ? '99+' : n.badge}</span>}
                  <Icon name="chevron" className={`nav-caret${isOpen(n) ? ' open' : ''}`} />
                </button>
                {isOpen(n) && (
                  <div className="nav-sub">
                    {n.children.map((c) => (
                      <NavLink key={c.to} to={c.to} end={c.end} title={c.label}>
                        <Icon name={c.icon} />
                        <span className="nav-text">{c.label}</span>
                        {c.badge > 0 && <span className="nav-badge">{c.badge > 99 ? '99+' : c.badge}</span>}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink key={n.to} to={n.to} end={n.end} title={n.label}>
                <Icon name={n.icon} />
                <span className="nav-text">{n.label}</span>
                {n.badge > 0 && <span className="nav-badge">{n.badge > 99 ? '99+' : n.badge}</span>}
              </NavLink>
            )
          )}
        </nav>

        <div className="sidebar-foot">
          <Avatar name={user?.name} />
          <div className="meta">
            <strong>{user?.name}</strong>
            <span>{isSuperAdmin ? 'Super Admin' : 'Admin'}</span>
          </div>
          <NavLink to="/settings" className="icon-btn settings-link" title="Settings" aria-label="Settings">
            <Icon name="settings" />
          </NavLink>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <button
            className="icon-btn only-mobile"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            <Icon name="menu" />
          </button>
          <button
            className="icon-btn only-desktop"
            onClick={() => setCollapsed((c) => !c)}
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
          >
            <Icon name="menu" />
          </button>
          <div className="crumbs">{current?.label || crmName}</div>
          <div className="spacer" />
          <button
            className="icon-btn"
            onClick={toggleTheme}
            aria-label="Toggle theme"
            title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
          </button>
          <button className="btn btn-sm" onClick={logout}>
            <Icon name="logout" size={15} />
            Log out
          </button>
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
